/**
 * Helpers for reading errors from the standard API envelope:
 * { meta, error: { status, code, message, debugMessage, packages }, isSuccess: false }
 *
 * Error responses are not unwrapped by installAuthToken, so call sites read
 * them here.
 */

/**
 * The user-facing message for an error thrown by axios, the authenticated
 * client or errorFromResponse. Falls back to `fallback` when the error
 * carries nothing better than axios' generic "Request failed with status code".
 */
export const getApiErrorMessage = (err, fallback = "An unexpected error occurred") => {
  const body = err?.response?.data;
  if (body?.error?.message) return body.error.message;
  if (typeof body?.error === "string") return body.error;
  if (body?.message) return body.message;
  if (err?.message && !/^Request failed with status code/.test(err.message)) return err.message;
  return fallback;
};

/** The envelope error code (e.g. "READ_ONLY"), if any. */
export const getApiErrorCode = (err) =>
  err?.response?.data?.error?.code || err?.code;

/** 403 READ_ONLY: the user's organisation doesn't own the record. */
export const isReadOnlyError = (err) => getApiErrorCode(err) === "READ_ONLY";

/**
 * Builds an Error from a failed fetch Response, using the envelope message.
 * Usage: if (!res.ok) throw await errorFromResponse(res, "Failed to save");
 */
export const errorFromResponse = async (response, fallback) => {
  let body = null;
  try {
    body = await response.clone().json();
  } catch {
    // not JSON
  }
  const apiError = body?.error;
  const message =
    apiError?.message ||
    (typeof apiError === "string" ? apiError : null) ||
    body?.message ||
    body?.title ||
    fallback ||
    `HTTP ${response.status}`;

  const error = new Error(message);
  error.status = response.status;
  error.code = apiError?.code;
  error.response = { status: response.status, data: body };
  return error;
};
